import type { PaletteOptions } from '@mui/material/styles';
import { tableSurface } from './tokens';

declare module '@mui/material/styles' {
  interface TypeBackground {
    elevated: string;
    /** Spine della navigazione, un gradino sotto il canvas. */
    rail: string;
  }
  interface Palette {
    severity: {
      critical: string;
      high: string;
      medium: string;
      low: string;
      info: string;
    };
    surface: {
      row: {
        default: string;
        hover: string;
        selected: string;
        /** Read-row hover. Used only when a table passes `highlightUnread`. */
        readHover: string;
      };
    };
    border: {
      hairline: string;
    };
  }
  interface PaletteOptions {
    severity?: Palette['severity'];
    surface?: Palette['surface'];
    border?: Palette['border'];
  }
}

/**
 * Console operativa. Light: bianco freddo e blu medio, più chiaro di un navy.
 * Dark: nero enterprise, stesso blu schiarito così resta leggibile sul nero.
 * Rosso, ambra e teal degli stati non cambiano.
 */
export const accent = {
  light: '#3A74B8',
  dark: '#7EAEF5',
} as const;
export const severity = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#f59e0b',
  low: '#3b82f6',
  info: '#64748b',
} as const;

export const darkPalette: PaletteOptions = {
  mode: 'dark',
  background: {
    default: '#0B0C0F',
    paper: '#15171C',
    elevated: '#1E2128',
    rail: '#070809',
  },
  divider: '#606978',
  text: {
    primary: '#F3F5F8',
    secondary: '#B6BEC8',
    disabled: '#6E7682',
  },
  primary: {
    main: accent.dark,
    light: '#A9C9F8',
    dark: '#5E96E4',
    contrastText: '#0C1420',
  },
  secondary: {
    main: '#A8B1BC',
    light: '#C5CDD6',
    dark: '#8B95A3',
    contrastText: '#0C1420',
  },
  success: { main: '#22c55e', light: '#4ade80', dark: '#16a34a', contrastText: '#052e16' },
  warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706', contrastText: '#1c1404' },
  error: { main: '#ef4444', light: '#f87171', dark: '#dc2626', contrastText: '#ffffff' },
  info: { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb', contrastText: '#ffffff' },
  severity: { ...severity },
  surface: { row: { ...tableSurface.dark.row } },
  border: { hairline: tableSurface.dark.hairline },
};

export const lightPalette: PaletteOptions = {
  mode: 'light',
  background: {
    default: '#F8FAFC',
    paper: '#ffffff',
    elevated: '#EEF3F8',
    rail: '#ffffff',
  },
  divider: '#7D8B9C',
  text: {
    primary: '#1B2430',
    secondary: '#526070',
    disabled: '#8A93A0',
  },
  primary: {
    main: accent.light,
    light: '#6BA0D8',
    dark: '#2E629E',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#526070',
    light: '#6E7C8C',
    dark: '#3E4C5C',
    contrastText: '#ffffff',
  },
  success: { main: '#16a34a', light: '#22c55e', dark: '#15803d', contrastText: '#ffffff' },
  warning: { main: '#d97706', light: '#f59e0b', dark: '#b45309', contrastText: '#1c1404' },
  error: { main: '#dc2626', light: '#ef4444', dark: '#b91c1c', contrastText: '#ffffff' },
  info: { main: '#2563eb', light: '#3b82f6', dark: '#1d4ed8', contrastText: '#ffffff' },
  severity: { ...severity },
  surface: { row: { ...tableSurface.light.row } },
  border: { hairline: tableSurface.light.hairline },
};
